import { Inject, Injectable } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { Datastore } from '@freelancer/datastore';
import type { UsersSelfCollection } from '@freelancer/datastore/collections';
import {
  NATIVE_LANGUAGE_COOKIE,
  WEBAPP_LANGUAGE_COOKIE,
} from '@freelancer/localization';
import { Pwa } from '@freelancer/pwa';
import { Tracking } from '@freelancer/tracking';
import { CookieService } from 'ngx-cookie';

@Injectable()
export class LanguageSwitcher {
  constructor(
    private auth: Auth,
    private cookies: CookieService,
    private datastore: Datastore,
    private tracking: Tracking,
    private pwa: Pwa,
    @Inject(NATIVE_LANGUAGE_COOKIE) private nativeLanguageCookie: string,
    @Inject(WEBAPP_LANGUAGE_COOKIE) private webappLanguageCookie: string,
  ) {}

  /**
   * Update a page to show in the specified language
   */
  switchLanguage(languageCode: string): void {
    this.datastore
      .document<UsersSelfCollection>('usersSelf', this.auth.getUserId())
      .update({
        primaryLanguage: languageCode,
      })
      .then(async () => {
        this.tracking.trackCustomEvent(
          'ChangeLanguageData',
          undefined, // section
          {
            language: languageCode,
          },
        );

        this.cookies.put(
          this.pwa.isNative()
            ? this.nativeLanguageCookie
            : this.webappLanguageCookie,
          languageCode,
        );

        if (!(await this.isDisabled())) {
          if (this.pwa.getPlatform() === 'ios') {
            // TODO: T263064. This one is being kept around as we have not
            // updated the Service Worker plugin to a Capacitor 3 plugin
            // just yet.
            // eslint-disable-next-line deprecation/deprecation
            const { ServiceWorker } = await this.pwa.capacitorPlugins();
            await ServiceWorker.switchLanguage({ languageCode });
          } else if (this.pwa.getPlatform() === 'android') {
            // TODO: T263064. This one is being kept around as we have not
            // updated the Service Worker plugin to a Capacitor 3 plugin
            // just yet.
            // eslint-disable-next-line deprecation/deprecation
            const { ServiceWorkerPlugin } = await this.pwa.capacitorPlugins();
            await ServiceWorkerPlugin.switchLanguage({ languageCode });
          }
        }

        window.location.reload();
      });
  }

  async isDisabled(): Promise<boolean> {
    return (
      this.pwa.isNative() &&
      !(await this.pwa.hasMinimumNativeAppVersion({ major: 4, minor: 6 }))
    );
  }
}
